import { memo, type ComponentProps } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { remarkInlineMarks } from "./remarkInlineMarks";

type ReactMarkdownProps = ComponentProps<typeof ReactMarkdown>;

/**
 * Shared prose styling for message markdown in the thread views: compact type,
 * wrapped code blocks, brand-red links and quoted-email borders.
 */
export const MESSAGE_PROSE_CLASS =
  "prose prose-sm text-sm min-w-0 max-w-none break-words dark:prose-invert prose-pre:whitespace-pre-wrap prose-pre:break-words prose-blockquote:border-l-primary/60 prose-a:text-primary dark:prose-a:text-primary/75";

type MarkdownReactProps = Omit<ReactMarkdownProps, "remarkPlugins"> & {
  /** Extra remark plugins appended after `remark-gfm`. */
  extraRemarkPlugins?: ReactMarkdownProps["remarkPlugins"];
};

/**
 * Memoized `react-markdown` wrapper, rendering GitHub-flavoured markdown plus
 * the `<u>`/`<b>`/`<i>` inline HTML markdown carries formatting as when its
 * own syntax cannot express it. Markdown parsing (micromark) is the dominant
 * cost when rendering long threads; memoizing so an unchanged `children` string
 * skips a re-parse when an ancestor re-renders is the single highest-leverage
 * perf win for the thread view.
 */
export const MarkdownReact = memo(function MarkdownReact({
  extraRemarkPlugins,
  ...props
}: MarkdownReactProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[
        remarkGfm,
        remarkInlineMarks,
        ...(extraRemarkPlugins ?? []),
      ]}
      {...props}
    />
  );
});
